import { errorWords } from "@alm/domain";
import { createFileRoute } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Plus, Trash2 } from "@/lib/icons";
import { useState } from "react";
import { toast } from "sonner";
import { EmptyState } from "@/components/app/lists/empty-state";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { SharedRowNote } from "@/components/app/lists/shared-row-note";
import { RowActions } from "@/components/app/patterns/row-actions";
import { DataTable } from "@/components/app/patterns/data-table";
import { DataTableToolbar } from "@/components/app/patterns/data-table-toolbar";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useFacility } from "@/lib/facility/facility";
import { orpc, orpcClient, type Output } from "@/lib/orpc";
import { useCan, useCanOrganizationWide } from "@/lib/policy";
import { ApplyTemplateDialog } from "./-components/apply-template-dialog";
import { TemplateDialog } from "./-components/template-dialog";
import type { TemplateRecord } from "@/routes/_authenticated/_workforce/training/-components/template-dialog-types";

type TemplateRow = Output<typeof orpcClient.workforce.training.listTemplates>[number];

const TEMPLATE_COLUMNS: ColumnDef<TemplateRow>[] = [
  {
    accessorKey: "name",
    header: "Template",
    enableHiding: false,
    meta: { clamp: "md", className: "font-medium" },
  },
  {
    id: "courses",
    header: "Courses",
    accessorFn: (template) => template.items.length,
    meta: { align: "right" },
  },
  {
    // A count says how many; the titles say whether it is the right bundle to hand somebody.
    id: "contents",
    header: "What is in it",
    accessorFn: (template) =>
      template.items.map((item) => item.courseTitle).join(", ") || "Nothing yet",
    enableSorting: false,
    meta: { priority: 2, clamp: "lg", className: "text-muted-foreground" },
  },
  {
    id: "onHire",
    header: "On hire",
    accessorFn: (template) => (template.autoAssignOnHire ? "Assigned automatically" : "By hand"),
    meta: { filter: "select" },
  },
];

export const Route = createFileRoute("/_authenticated/_workforce/training/templates")({
  component: TrainingTemplates,
});

/** Named bundles of courses. The one that earns its own screen is auto-assign-on-hire: it runs when
 * an applicant becomes an employee, so what it contains is what a new starter is held to on their
 * first day. */
function TrainingTemplates() {
  const { facilityId } = useFacility();
  // Every building shares a template; handing one out is done at the building in view.
  const canManage = useCanOrganizationWide("training:manage");
  const canAssign = useCan("training:manage");
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<TemplateRecord | null>(null);
  const [open, setOpen] = useState(false);
  const [removing, setRemoving] = useState<TemplateRecord | null>(null);
  const [applying, setApplying] = useState<TemplateRecord | null>(null);

  const templates = useQuery(orpc.workforce.training.listTemplates.queryOptions({}));
  const courses = useQuery(orpc.workforce.training.listCourses.queryOptions({ input: {} }));

  const remove = useMutation(
    orpc.workforce.training.deleteTemplate.mutationOptions({
      onSuccess: async () => {
        toast.success("Template deleted");
        setRemoving(null);
        await queryClient.invalidateQueries({ queryKey: orpc.workforce.training.key() });
      },
      onError: (error) => toast.error(errorWords(error)),
    }),
  );

  const rows = templates.data ?? [];

  return (
    <>
      <PageHeader
        title="Templates"
        count={templates.data ? rows.length : undefined}
        meta="Sets of courses given together, and which of them a new starter gets on their own."
        actions={
          canManage ? (
            <Button
              onClick={() => {
                setEditing(null);
                setOpen(true);
              }}
            >
              <Plus className="size-4" />
              New template
            </Button>
          ) : canAssign ? (
            <SharedRowNote />
          ) : null
        }
      />

      {templates.isPending ? (
        <Skeleton className="h-64 w-full" />
      ) : templates.isError ? (
        <ReadFailed
          message="Could not load the training templates."
          error={templates.error}
          onRetry={() => void templates.refetch()}
          retrying={templates.isFetching}
        />
      ) : rows.length === 0 ? (
        <EmptyState
          title="No templates yet"
          description="Bundle the courses a new caregiver has to take, and they can be assigned in one action."
        />
      ) : (
        <DataTable
          data={rows}
          columns={TEMPLATE_COLUMNS}
          noun="templates"
          rowId={(template) => template.id}
          renderToolbar={(table) => (
            <DataTableToolbar table={table} searchPlaceholder="Search templates…" />
          )}
          renderRowActions={
            canManage || canAssign
              ? (template) => (
                  <span className="flex items-center justify-end gap-2">
                    {canManage ? null : <SharedRowNote />}
                    <RowActions
                      modal={false}
                      label={template.name}
                      primary={
                        canAssign && { label: "Assign", onSelect: () => setApplying(template) }
                      }
                      items={[
                        canManage && {
                          label: "Edit template",
                          icon: Pencil,
                          onSelect: () => {
                            setEditing(template);
                            setOpen(true);
                          },
                        },
                        canManage && {
                          label: "Delete template",
                          icon: Trash2,
                          destructive: true,
                          onSelect: () => setRemoving(template),
                        },
                      ]}
                    />
                  </span>
                )
              : undefined
          }
        />
      )}

      <ApplyTemplateDialog
        facilityId={facilityId}
        template={applying}
        onClose={() => setApplying(null)}
      />

      <TemplateDialog
        courses={courses.data ?? []}
        onOpenChange={setOpen}
        open={open}
        template={editing}
      />

      <AlertDialog open={removing !== null} onOpenChange={(next) => !next && setRemoving(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete “{removing?.name}”?</AlertDialogTitle>
            <AlertDialogDescription>
              Courses already assigned from it stay assigned. Only the bundle goes.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep template</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              disabled={remove.isPending}
              onClick={() => removing && remove.mutate({ templateId: removing.id })}
            >
              Delete template
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
